import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { encodeLinkKey, generateLinkKey, sealSecret } from '@sss/crypto';
import { CHALLENGE_ACTIONS, ROOM_LIMITS } from '@sss/types';
import FlameMark from '../components/Brand/FlameMark';
import HumanCheck from '../components/Secret/HumanCheck';
import RelayPassField from '../components/Secret/RelayPassField';
import { LinkStep, PassphraseStep } from '../components/Secret/ShareSteps';
import secretStyles from '../components/Secret/Secret.module.css';
import { useHumanCheck } from '../hooks/useHumanCheck';
import { usePass } from '../hooks/usePass';
import { usePageTitle } from '../hooks/usePageTitle';
import { ICE_SERVERS } from '../lib/config';
import { FileSender } from '../lib/fileHandoff';
import { MAX_FILES, RELAY_MAX_TOTAL_BYTES } from '../lib/fileTransfer';
import { HandoffConnection, type Transport } from '../lib/handoffConnection';
import { formatCode } from '../lib/handoffProtocol';
import { createRelayGrant, relayProblem, settleRelayGrant } from '../lib/relayApi';
import { createRoom, roomSocketUrl } from '../lib/roomsApi';
import { MAX_SECRET_BYTES, checkSecretText, formatKilobytes, utf8ByteLength } from '../lib/secretForm';
import { buildFilesLink, buildHandoffLink } from '../lib/secretLinks';
import { SecretsApiError } from '../lib/secretsApi';
import { formatBytes } from '../lib/tools';
import ui from '../styles/ui.module.css';
import createStyles from './CreatePage.module.css';
import styles from './LivePage.module.css';

/**
 * /live — hand one secret, or files, to one person while both are online
 * (docs/adr/0010, 0011). A text secret is sealed here as soon as the
 * handoff starts and this tab keeps only the sealed payload; files stay on
 * the device and are read chunk by chunk as they're sent. Either goes when
 * the recipient connects (after the code check, if asked). Nothing is
 * stored on the server. Files over 25 MB can go through Cloudflare's TURN
 * relay with a relay pass (docs/adr/0013): the grant is settled when the
 * handoff ends, and charged from Cloudflare's own measurement, never from
 * anything this page reports. The send wizard (/send, docs/adr/0014)
 * embeds the same panel with the kind fixed, and its Relay choice keeps the
 * sender's IP address from the recipient.
 */

/** How long a transfer that can only go direct waits for the connection (ADR 0011). */
const DIRECT_ONLY_TIMEOUT_MS = 30_000;

type WaitSeconds = (typeof ROOM_LIMITS.waitSeconds)[number];
const DEFAULT_WAIT: WaitSeconds = 900;

type Kind = 'text' | 'files';
/** `relay`: always through our servers, so the recipient never learns the sender's address. */
export type LiveRoute = 'auto' | 'relay';
type Stage = 'connecting' | 'waiting' | 'securing' | 'confirm' | 'sending' | 'offered' | 'transferring' | 'delivered' | 'done';
type Outcome = 'acknowledged' | 'deliveredOnly' | 'cancelled' | 'expired' | 'relayLimit' | 'lost';

interface Live {
  kind: Kind;
  link: string;
  passphrase: string;
  stage: Stage;
  code: string | null;
  transport: Transport | null;
  /** The direct channel goes through the paid TURN relay. */
  viaRelay: boolean;
  /** The room's current deadline: the link's expiry while waiting. */
  deadline: string;
  /** The recipient left before anything was sent. */
  left: boolean;
  /** Files: the last pairing had no direct connection, so nothing was sent; the recipient can try again. */
  noDirect: boolean;
  deliveredAt: number | null;
  outcome: Outcome | null;
  outcomeAt: number | null;
  /** Files: bytes the recipient has written, of the total. */
  progress: { written: number; total: number } | null;
}

export default function LiveSendPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  usePageTitle(t('pageTitle.live'));

  return (
    <main className={createStyles.page}>
      <div className={createStyles.grid}>
        <section className={createStyles.hero} aria-labelledby="live-title">
          <h1 id="live-title" className={createStyles.title}>
            {t('live.titleLine1')}
            <br />
            {t('live.titleLine2')}
            <br />
            <span className={createStyles.titleAccent}>{t('live.titleLine3')}</span>
          </h1>
          <p className={createStyles.lead}>{t('live.lead')}</p>
          <ol className={createStyles.lifecycle} aria-label={t('live.lifecycleLabel')}>
            <li>{t('live.lifecycle.encrypted')}</li>
            <li aria-hidden="true" className={createStyles.fuse} />
            <li>{t('live.lifecycle.connected')}</li>
            <li aria-hidden="true" className={createStyles.fuse} />
            <li>{t('live.lifecycle.received')}</li>
            <li aria-hidden="true" className={`${createStyles.fuse} ${createStyles.fuseLit}`} />
            <li className={createStyles.burned}>{t('live.lifecycle.confirmed')}</li>
          </ol>
          <p className={createStyles.codeLink}>
            <Link to="/new">{t('live.burnLink')}</Link>
          </p>
        </section>
        <LivePanel initialKind={params.get('kind') === 'files' ? 'files' : 'text'} />
      </div>
    </main>
  );
}

interface LivePanelProps {
  /** Fixed by the send wizard: no text/files switch is shown. */
  kind?: Kind;
  initialKind?: Kind;
  route?: LiveRoute;
}

/** The live handoff form, then its status. Used by /live and by the send wizard. */
export function LivePanel({ kind: fixedKind, initialKind = 'text', route = 'auto' }: LivePanelProps) {
  const { t, i18n } = useTranslation();
  const [chosenKind, setKind] = useState<Kind>(fixedKind ?? initialKind);
  const kind = fixedKind ?? chosenKind;
  const hideAddress = route === 'relay';
  const [secret, setSecret] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const filesTotal = files.reduce((sum, f) => sum + f.size, 0);
  const [passphrase, setPassphrase] = useState('');
  const [wait, setWait] = useState<WaitSeconds>(DEFAULT_WAIT);
  const [checkFirst, setCheckFirst] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [secretInvalid, setSecretInvalid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [live, setLive] = useState<Live | null>(null);
  // Shared with the header and other tabs: a pass bought in its own tab (/pass/bought) shows up here.
  const [pass, changePass] = usePass();
  const humanCheck = useHumanCheck(CHALLENGE_ACTIONS.createRoom);
  const connection = useRef<HandoffConnection | null>(null);
  // The sealed secret, until it's delivered. Never the plaintext.
  const payload = useRef<Uint8Array | null>(null);
  // Files: what to send, and the transfer to the current recipient.
  const outgoing = useRef<File[]>([]);
  const fileSender = useRef<FileSender | null>(null);
  // The relay grant this handoff holds.
  const grant = useRef<{ id: string; pass: string } | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  const time = (at: string | number) => new Date(at).toLocaleTimeString(i18n.language, { hour: 'numeric', minute: '2-digit' });
  // Once there's an outcome, late events (the recipient's socket closing, say) change nothing.
  const update = (patch: Partial<Live> | ((live: Live) => Partial<Live>)) =>
    setLive((current) => (current && !current.outcome ? { ...current, ...(typeof patch === 'function' ? patch(current) : patch) } : current));
  // Once per grant: end it, so its credentials are revoked. Cloudflare's measurement decides the charge.
  const settleRelay = () => {
    const held = grant.current;
    if (!held) return;
    grant.current = null;
    void settleRelayGrant(held.id, { pass: held.pass });
  };
  const finish = (outcome: Outcome) => {
    settleRelay();
    payload.current = null;
    outgoing.current = [];
    fileSender.current?.stop();
    connection.current?.close();
    update({ stage: 'done', outcome, outcomeAt: Date.now() });
  };

  const active = live !== null && live.stage !== 'done';

  // Closing or reloading the tab loses the secret, so ask first.
  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onBeforeUnload);
    // If they leave anyway, settle the relay grant on the way out.
    window.addEventListener('pagehide', settleRelay);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', settleRelay);
    };
  }, [active]);

  // Leaving the page within the app ends the handoff: the payload goes with it.
  useEffect(
    () => () => {
      settleRelay();
      connection.current?.notify('cancel');
      connection.current?.close();
    },
    [],
  );

  useEffect(() => {
    if (live?.stage === 'done') heading.current?.focus();
  }, [live?.stage]);

  async function send() {
    const conn = connection.current;
    if (!conn) return;
    if (outgoing.current.length > 0) {
      // Offer the files; chunks follow once the recipient picks where to save them.
      fileSender.current?.stop();
      const sender = new FileSender(conn, outgoing.current, (written) => {
        update((current) => ({ stage: current.stage === 'delivered' ? 'delivered' : 'transferring', progress: { written, total: sender.total } }));
      });
      fileSender.current = sender;
      update({ stage: 'offered', progress: { written: 0, total: sender.total } });
      await sender.offer();
      return;
    }
    const sealed = payload.current;
    if (!sealed) return;
    update({ stage: 'sending' });
    await conn.send('secret', sealed);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (kind === 'files') {
      if (files.length === 0 || files.length > MAX_FILES) {
        setProblem(t(files.length === 0 ? 'live.errors.noFiles' : 'live.errors.tooManyFiles', { max: MAX_FILES }));
        return;
      }
    } else {
      const check = checkSecretText(secret);
      setSecretInvalid(Boolean(check));
      if (check) {
        setProblem(t(`create.errors.${check}`, { max: formatKilobytes(MAX_SECRET_BYTES) }));
        return;
      }
    }
    // Files the room can't carry have nothing to fall back to, except the paid relay.
    const directOnly = kind === 'files' && filesTotal > RELAY_MAX_TOTAL_BYTES;
    // Going direct would show the recipient our address, so the relay is the only way.
    if (hideAddress && directOnly && !pass) {
      setProblem(t('live.errors.relayNeedsPass'));
      return;
    }
    const turnstileToken = humanCheck.take();
    if (!turnstileToken) {
      setProblem(t(humanCheck.state === 'failed' || humanCheck.state === 'unavailable' ? 'live.errors.checkFailed' : 'create.errors.checkPending'));
      return;
    }
    setProblem(null);
    setBusy(true);
    const relayPass = directOnly ? pass : null;
    try {
      let relay;
      let peerRelay;
      if (relayPass) {
        try {
          const issued = await createRelayGrant({ pass: relayPass, bytes: filesTotal });
          grant.current = { id: issued.id, pass: relayPass };
          relay = issued.iceServer;
          peerRelay = issued.peerIceServer;
        } catch (err) {
          setProblem(t(`live.relayPass.problems.${relayProblem(err)}`));
          return;
        }
      }
      // Sealed now, with the key that goes in the link (docs/crypto-spec.md §5.2).
      const roomKey = generateLinkKey();
      const sealed = kind === 'text' ? await sealSecret(secret, roomKey, { passphrase: passphrase || undefined }) : null;
      const room = await createRoom({ waitSeconds: wait, turnstileToken });
      const origin = window.location.origin;
      payload.current = sealed;
      outgoing.current = kind === 'files' ? files : [];
      const sendFirst = !checkFirst;
      const conn = new HandoffConnection({
        roomId: room.id,
        roomKey,
        role: 'sender',
        senderToken: room.senderToken,
        socketUrl: roomSocketUrl(room.id, 'sender', origin),
        iceServers: ICE_SERVERS,
        relay,
        peerRelay,
        hideAddress,
        // Nothing to fall back to, so give a slow direct connection longer.
        directTimeoutMs: directOnly ? DIRECT_ONLY_TIMEOUT_MS : undefined,
        events: {
          joined: (deadline) => update((current) => (current.stage === 'connecting' ? { stage: 'waiting', deadline } : {})),
          peer: (present, deadline) => {
            // A transfer to someone who left can't resume; a new arrival gets a fresh one.
            fileSender.current?.stop();
            if (present) update({ stage: 'securing', code: null, transport: null, viaRelay: false, left: false, noDirect: false, progress: null });
            else
              update((current) =>
                current.deliveredAt ? {} : { stage: 'waiting', code: null, transport: null, viaRelay: false, left: true, deadline, progress: null },
              );
          },
          secure: (code) => {
            update({ code, stage: sendFirst ? 'sending' : 'confirm' });
            if (sendFirst) void send();
          },
          path: (viaRelay) => update({ viaRelay }),
          transport: (transport) => {
            update({ transport });
            // The room relays only a little (ADR 0011); bigger files need a direct connection.
            // Send nothing more to this recipient. The file list (already queued) tells their
            // page why; they leave, and the link stays open for them to try again.
            if (directOnly && transport === 'relay') {
              fileSender.current?.stop();
              fileSender.current = null;
              update({ stage: 'waiting', code: null, noDirect: true, progress: null });
            }
          },
          record: (type, body) => {
            if (type === 'progress') {
              // A recipient told there's no direct connection doesn't accept; ignore one that does.
              if (!fileSender.current) return;
              if (!fileSender.current?.progress(body)) {
                conn.notify('cancel');
                finish('lost');
              }
            } else if (type === 'delivered') {
              payload.current = null;
              outgoing.current = [];
              conn.notify('delivered');
              update({ stage: 'delivered', deliveredAt: Date.now() });
            } else if (type === 'acknowledged') {
              conn.notify('complete');
              finish('acknowledged');
            }
          },
          ended: (reason) => {
            if (reason === 'acknowledged') finish('acknowledged');
            else if (reason === 'delivered') finish('deliveredOnly');
            else if (reason === 'cancelled' || reason === 'expired' || reason === 'relayLimit') finish(reason);
            else finish('lost');
          },
          unavailable: () => finish('lost'),
          lost: () => finish(payload.current ? 'lost' : 'deliveredOnly'),
        },
      });
      connection.current = conn;
      setLive({
        kind,
        link: (kind === 'files' ? buildFilesLink : buildHandoffLink)(origin, room.id, encodeLinkKey(roomKey)),
        passphrase: kind === 'text' ? passphrase : '',
        stage: 'connecting',
        code: null,
        transport: null,
        viaRelay: false,
        deadline: room.expiresAt,
        left: false,
        noDirect: false,
        deliveredAt: null,
        outcome: null,
        outcomeAt: null,
        progress: null,
      });
      conn.connect();
      setSecret('');
      setPassphrase('');
      setFiles([]);
    } catch (err) {
      settleRelay();
      payload.current = null;
      outgoing.current = [];
      const type = err instanceof SecretsApiError ? err.type : 'other';
      if (type === 'rateLimited' || type === 'challengeFailed') setProblem(t(`live.errors.${type}`));
      else if (type === 'network') setProblem(t('create.errors.network'));
      else setProblem(t('live.errors.failed'));
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    connection.current?.notify('cancel');
    finish('cancelled');
  }

  function startAgain() {
    connection.current = null;
    setLive(null);
    setWait(DEFAULT_WAIT);
    setCheckFirst(false);
  }

  const bytes = utf8ByteLength(secret);

  return (
    <>
      {live ? (
        <div className={createStyles.result}>
          <LiveStatus live={live} time={time} heading={heading} onSend={() => void send()} onCancel={cancel} onAnother={startAgain} />
        </div>
      ) : (
        <form className={`${ui.panel} ${createStyles.form}`} onSubmit={onSubmit} noValidate aria-labelledby="live-form-title" aria-busy={busy}>
          <div className={createStyles.formHeader}>
            <h2 id="live-form-title" className={createStyles.formTitle}>
              {t(hideAddress ? 'live.form.titleRelay' : 'live.form.title')}
            </h2>
            <span className={ui.badge}>{t(hideAddress ? 'live.form.badgeRelay' : 'live.form.badge')}</span>
          </div>

          {!fixedKind && (
            <fieldset className={styles.kind}>
              <legend className="visually-hidden">{t('live.form.kindLegend')}</legend>
              {(['text', 'files'] as const).map((k) => (
                <label key={k} className={styles.kindOption}>
                  <input
                    type="radio"
                    name="live-kind"
                    value={k}
                    checked={kind === k}
                    onChange={() => {
                      setKind(k);
                      setProblem(null);
                    }}
                  />
                  <span>{t(`live.form.kind.${k}`)}</span>
                </label>
              ))}
            </fieldset>
          )}

          {kind === 'files' && (
            <label className={ui.field}>
              {t('live.form.filesLabel')}
              <input type="file" multiple className={styles.file} onChange={(e) => setFiles([...(e.target.files ?? [])])} aria-describedby="live-files-hint" />
              <span id="live-files-hint" className={ui.hint}>
                {files.length > 0
                  ? t('live.form.filesChosen', { count: files.length, size: formatBytes(filesTotal) })
                  : t('live.form.filesHint', { max: MAX_FILES })}
                {filesTotal > RELAY_MAX_TOTAL_BYTES && !pass && !hideAddress && <span className={styles.hintNote}> {t('live.form.filesDirectOnly')}</span>}
              </span>
            </label>
          )}
          {kind === 'files' && filesTotal > RELAY_MAX_TOTAL_BYTES && <RelayPassField pass={pass} onChange={changePass} needed={filesTotal} />}
          {kind === 'text' && (
            <>
              <label className={ui.field}>
                {t('create.form.secretLabel')}
                <textarea
                  rows={6}
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder={t('create.form.secretPlaceholder')}
                  aria-describedby={secretInvalid ? 'live-size live-problem' : 'live-size'}
                  aria-invalid={secretInvalid || undefined}
                  autoComplete="off"
                  spellCheck={false}
                  className={createStyles.secret}
                />
              </label>
              <p id="live-size" className={`${ui.hint} ${createStyles.size}`} aria-live="polite">
                {bytes > MAX_SECRET_BYTES * 0.8
                  ? t('create.form.size', { used: formatKilobytes(bytes), max: formatKilobytes(MAX_SECRET_BYTES) })
                  : t('create.form.sizeHint', { max: formatKilobytes(MAX_SECRET_BYTES) })}
              </p>
            </>
          )}

          <div className={createStyles.row}>
            <label className={ui.field}>
              {t('live.form.waitLabel')}
              <select value={wait} onChange={(e) => setWait(Number(e.target.value) as WaitSeconds)}>
                {ROOM_LIMITS.waitSeconds.map((seconds) => (
                  <option key={seconds} value={seconds}>
                    {t(`live.form.wait.${seconds}`)}
                  </option>
                ))}
              </select>
            </label>
            {kind === 'text' && (
              <label className={ui.field}>
                {t('create.form.passphraseLabel')}
                <input
                  type="password"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  placeholder={t('create.form.passphrasePlaceholder')}
                  autoComplete="new-password"
                />
                <span className={ui.hint}>{t('create.form.passphraseHint')}</span>
              </label>
            )}
          </div>

          <label className={styles.check}>
            <input type="checkbox" checked={checkFirst} onChange={(e) => setCheckFirst(e.target.checked)} aria-describedby="check-first-hint" />
            <span>
              {t('live.form.checkFirst')}
              <span id="check-first-hint" className={ui.hint}>
                {t('live.form.checkFirstHint')}
              </span>
            </span>
          </label>

          <HumanCheck check={humanCheck} />

          {problem && (
            <p role="alert" id="live-problem" className={ui.error}>
              {problem}
            </p>
          )}

          <button type="submit" className={ui.button} disabled={busy}>
            <FlameMark size={18} tone="onAccent" />
            {busy ? t('live.form.submitting') : t('live.form.submit')}
          </button>
          <p className={`${ui.hint} ${createStyles.terms}`}>
            {hideAddress
              ? t('live.form.termsRelay')
              : kind === 'files'
                ? t('live.form.termsFiles')
                : t('live.form.terms', { max: formatKilobytes(MAX_SECRET_BYTES) })}
          </p>
        </form>
      )}
    </>
  );
}

interface LiveStatusProps {
  live: Live;
  time: (at: string | number) => string;
  heading: React.RefObject<HTMLHeadingElement | null>;
  onSend: () => void;
  onCancel: () => void;
  onAnother: () => void;
}

function LiveStatus({ live, time, heading, onSend, onCancel, onAnother }: LiveStatusProps) {
  const { t } = useTranslation();
  const done = live.stage === 'done';
  const hasPassphrase = live.passphrase.length > 0;

  // The form is replaced, taking focus with it: start at the result.
  useEffect(() => heading.current?.focus(), [heading]);

  const line = (() => {
    if (live.outcome === 'acknowledged') return t('live.status.acknowledged', { time: time(live.outcomeAt ?? Date.now()) });
    if (live.outcome === 'deliveredOnly') return t('live.status.deliveredOnly', { time: time(live.deliveredAt ?? live.outcomeAt ?? Date.now()) });
    if (live.outcome) return t(`live.status.${live.outcome}`);
    if (live.noDirect) return t('live.status.noDirect', { time: time(live.deadline) });
    switch (live.stage) {
      case 'connecting':
        return t('live.status.connecting');
      case 'waiting':
        return live.left ? t('live.status.left', { time: time(live.deadline) }) : `${t('live.status.waiting')} ${t('live.status.waitingUntil', { time: time(live.deadline) })}`;
      case 'securing':
        return t('live.status.joined');
      case 'confirm':
        return t('live.status.checkPrompt');
      case 'sending':
        return t('live.status.sending');
      case 'offered':
        return t('live.status.offered');
      case 'transferring': {
        const { written, total } = live.progress ?? { written: 0, total: 0 };
        return t('live.status.transferring', { percent: tenths(written, total), done: formatBytes(written), total: formatBytes(total) });
      }
      default:
        return t('live.status.delivered');
    }
  })();

  return (
    <section className={styles.live} aria-labelledby="live-status-title">
      <header className={styles.liveHeader}>
        <h2 id="live-status-title" className={styles.liveTitle} tabIndex={-1} ref={heading}>
          {done ? t(live.outcome === 'acknowledged' || live.outcome === 'deliveredOnly' ? 'live.status.doneTitle' : 'live.status.stoppedTitle') : t('live.status.title')}
        </h2>
        {!done && <p className={secretStyles.notice}>{t(live.kind === 'files' ? 'live.status.keepOpenFiles' : 'live.status.keepOpen')}</p>}
      </header>

      <section className={styles.status} aria-label={t('live.status.label')}>
        <p className={styles.statusLine} role="status" data-testid="live-status">
          <span
            className={`${styles.dot} ${done ? styles.dotDone : live.stage === 'waiting' || live.stage === 'delivered' ? styles.dotWaiting : ''}`}
            aria-hidden="true"
          />
          {line}
        </p>
        {live.code && !done && (
          <div className={styles.code}>
            <span className={ui.eyebrow}>{t('live.status.code')}</span>
            <span className={styles.codeDigits} data-testid="live-code">
              {formatCode(live.code)}
            </span>
            <span className={ui.hint}>{t('live.status.codeHint')}</span>
          </div>
        )}
        {live.progress && live.stage === 'transferring' && (
          <progress className={styles.progress} value={live.progress.written} max={Math.max(live.progress.total, 1)} aria-label={t('live.status.progressLabel')} />
        )}
        {live.transport && !done && (
          <p className={styles.transport} data-testid="live-transport" data-transport={live.transport} data-via-relay={live.viaRelay}>
            {t(live.viaRelay ? 'live.status.turn' : `live.status.${live.transport}`)}
          </p>
        )}
        {done ? (
          <button type="button" className={ui.button} onClick={onAnother}>
            {t('live.status.another')}
          </button>
        ) : (
          <div className={styles.statusActions}>
            {live.stage === 'confirm' && (
              <button type="button" className={ui.button} onClick={onSend}>
                {t('live.status.send')}
              </button>
            )}
            {live.stage !== 'delivered' && (
              <button type="button" className={ui.buttonSecondary} onClick={onCancel}>
                {t('live.status.cancel')}
              </button>
            )}
          </div>
        )}
      </section>

      {!done && live.stage !== 'delivered' && live.stage !== 'transferring' && (
        <>
          <LinkStep
            link={live.link}
            step={hasPassphrase ? 1 : null}
            text={t(live.kind === 'files' ? 'live.share.filesText' : hasPassphrase ? 'live.share.linkTextPassphrase' : 'live.share.linkText')}
          />
          {hasPassphrase && (
            <>
              <p className={secretStyles.between} aria-hidden="true">
                <span>{t('secret.created.between')}</span>
              </p>
              <PassphraseStep passphrase={live.passphrase} />
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Whole percent done, rounded down to a multiple of 10 so the status line (a live region) changes rarely. */
const tenths = (written: number, total: number): number => (total > 0 ? Math.floor((written / total) * 10) * 10 : 0);
